/**
 * ViewSwitcher - component to switch between board views
 */

import { useTranslation } from 'react-i18next';

export type BoardViewType = 'kanban' | 'list' | 'timeline' | 'calendar' | 'table' | 'files';

interface ViewSwitcherProps {
  currentView: BoardViewType;
  onViewChange: (view: BoardViewType) => void;
}

// Module-level array: stores labelKey paths — no t() calls at module level
const views: { id: BoardViewType; labelKey: string; icon: string }[] = [
  {
    id: 'kanban',
    labelKey: 'settings.viewKanban',
    icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2',
  },
  {
    id: 'list',
    labelKey: 'settings.viewList',
    icon: 'M4 6h16M4 10h16M4 14h16M4 18h16',
  },
  {
    id: 'timeline',
    labelKey: 'settings.viewTimeline',
    icon: 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4',
  },
  {
    id: 'calendar',
    labelKey: 'settings.viewCalendar',
    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  },
  {
    id: 'table',
    labelKey: 'settings.viewTable',
    icon: 'M3 10h18M3 14h18M3 18h18M3 6h18',
  },
  {
    id: 'files',
    labelKey: 'settings.viewFiles',
    icon: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z',
  },
];

export function ViewSwitcher({ currentView, onViewChange }: ViewSwitcherProps) {
  const { t } = useTranslation();

  return (
    <div>
      <label className="sr-only" htmlFor="board-view">{t('board.viewLabel', 'Board view')}</label>
      <select id="board-view" value={currentView} onChange={(event) => onViewChange(event.target.value as BoardViewType)} className="min-h-11 max-w-40 rounded-lg border border-neutral-200 bg-white px-3 text-sm font-medium dark:border-neutral-700 dark:bg-neutral-900 sm:hidden">
        {views.map((view) => <option key={view.id} value={view.id}>{t(view.labelKey)}</option>)}
      </select>
      <div role="group" aria-label={t('board.viewLabel', 'Board view')} className="hidden items-center gap-0.5 rounded-lg bg-neutral-100 p-1 dark:bg-neutral-800 sm:flex">
      {views.map((view) => (
        <button
          key={view.id}
          aria-label={t(view.labelKey)}
          title={t(view.labelKey)}
          aria-pressed={currentView === view.id}
          onClick={() => onViewChange(view.id)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            currentView === view.id
              ? 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white shadow-sm'
              : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white'
          }`}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={view.icon}
            />
          </svg>
          <span className="hidden xl:inline">{t(view.labelKey)}</span>
        </button>
      ))}
      </div>
    </div>
  );
}
